import { computed, ref, watch, type ComputedRef, type Ref } from "vue";
import useHeaderHeight from "../../../../utils/headerHeight";
import {
  acquireOverlayScrollLock,
  getOverlayScrollLockCount,
  OVERLAY_LOCK_CHANGE_EVENT,
  // eslint-disable-next-line
} from "~/core/utils/overlayScrollLock.ts";
// eslint-disable-next-line
import { VF_LAYOUT_ANIMATION_EVENT } from "~/core/utils/layoutAnimation.ts";
import { MENU_NAVIGATION_DELAY } from "./useHeaderMenuState";

export function useHeaderScroll(deps: {
  isExternalOverlayActive: Ref<boolean>;
  isMobileMenuOpen: ComputedRef<boolean>;
  isInteractionActive: ComputedRef<boolean>;
  // Clears menu/search state when an external overlay (cart sidebar etc.) opens.
  onOverlayActivated: () => void;
  onMobileMenuReset: () => void;
}) {
  const {
    isExternalOverlayActive,
    isMobileMenuOpen,
    isInteractionActive,
    onOverlayActivated,
    onMobileMenuReset,
  } = deps;

  const headerRef = ref<HTMLElement | null>(null);
  const {
    headerHeight,
    visibleHeaderHeight,
    isHeaderHidden,
    setHeaderHeight,
    setHeaderHidden,
    setVisibleHeaderHeight,
    enableManualVisibleHeaderHeightControl,
  } = useHeaderHeight();
  const placeholderHeight = computed(() => headerHeight.value);
  const headerOffset = ref(0);
  const targetHeaderOffset = ref(0);
  const headerOpacity = computed(() => {
    const totalHeight = headerHeight.value;
    if (totalHeight <= 0) return 1;

    return Math.max(0, Math.min(1, 1 - headerOffset.value / totalHeight));
  });

  const renderedHeaderOffset = computed(() =>
    isMobileMenuOpen.value ? 0 : headerOffset.value
  );
  const renderedHeaderOpacity = computed(() =>
    isMobileMenuOpen.value ? 1 : headerOpacity.value
  );

  const overlayLockedVisibleHeight = ref<number | null>(null);
  // The compact menu holds the shared overlay lock like every other drawer, so
  // a cart opened from the menu can never capture or restore the menu's styles.
  let releaseMobileMenuScrollLock: (() => void) | null = null;

  const HEADER_SCROLL_STORAGE_KEY = "vf_header_scroll_state";
  const OFFSET_EASE = 0.5;
  const OFFSET_SNAP = 0.5;
  const MIN_SCROLL_TO_HIDE = 80;
  const TOP_EPSILON = 2;

  const lastScrollY = ref(0);
  let scrollRaf: number | null = null;
  let skipNextEvaluation = false;
  const lastDirection = ref<1 | -1 | 0>(0);
  let headerOffsetRaf: number | null = null;
  let scrollIdleTimeout: number | null = null;
  // iOS Safari fires scroll events while the DOCUMENT height is changing
  // (accordion open/close height transitions, content mounting) because it
  // adjusts the scroll position to the moving layout. Those events are not
  // user scrolling — reacting to them moves the header, which perturbs the
  // viewport again and produces another scroll event with the opposite delta:
  // a feedback loop that visibly shakes the whole page. We track the document
  // height and absorb any scroll event that arrives while the height is
  // mutating (plus a short grace period for the trailing event).
  let lastDocumentHeight = 0;
  let lastDocumentHeightChangeAt = 0;
  const DOC_MUTATION_SCROLL_GRACE_MS = 150;
  // Deterministic layer on top of the height-mutation detection above:
  // accordions announce their height animation via VF_LAYOUT_ANIMATION_EVENT
  // (see utils/layoutAnimation.ts). While the announced window (plus a buffer
  // for iOS Safari's ANIMATED post-transition scroll clamp, which keeps firing
  // scroll events after the document height has already settled) is active,
  // header scroll evaluation is frozen entirely.
  let layoutAnimationFreezeUntil = 0;
  const LAYOUT_ANIMATION_BUFFER_MS = 400; // covers post-animation scroll clamp

  const handleLayoutAnimationNotice = (event: Event) => {
    const detail = (event as CustomEvent<{ durationMs?: number }>).detail;
    const duration = Math.max(0, Number(detail?.durationMs || 0));
    const until = performance.now() + duration + LAYOUT_ANIMATION_BUFFER_MS;

    if (until > layoutAnimationFreezeUntil) {
      layoutAnimationFreezeUntil = until;
    }
  };

  let mobileMenuResetTimer: number | null = null;

  let resizeObserver: ResizeObserver | null = null;
  let releaseVisibleHeightControl: (() => void) | null = null;

  const isOverlayActiveNow = () =>
    isExternalOverlayActive.value || getOverlayScrollLockCount() > 0;

  const getHeaderTotalHeight = () => {
    const header = headerRef.value?.getElementsByClassName("header-wrapper")[0];
    return (
      (header as HTMLElement | undefined)?.offsetHeight ||
      headerHeight.value ||
      0
    );
  };

  // Freeze header geometry while overlay is open so mobile drawers do not inherit
  // a half-hidden header position during scroll lock/unlock.
  const freezeHeaderForOverlay = () => {
    if (typeof window === "undefined") return;

    if (headerOffsetRaf !== null) {
      window.cancelAnimationFrame(headerOffsetRaf);
      headerOffsetRaf = null;
    }

    if (scrollRaf !== null) {
      window.cancelAnimationFrame(scrollRaf);
      scrollRaf = null;
    }

    if (scrollIdleTimeout !== null) {
      window.clearTimeout(scrollIdleTimeout);
      scrollIdleTimeout = null;
    }

    const totalHeight = getHeaderTotalHeight();
    if (totalHeight <= 0) {
      overlayLockedVisibleHeight.value = visibleHeaderHeight.value;
      setVisibleHeaderHeight(overlayLockedVisibleHeight.value);
      return;
    }

    const clampedOffset = Math.max(
      0,
      Math.min(totalHeight, headerOffset.value)
    );
    headerOffset.value = clampedOffset;
    targetHeaderOffset.value = clampedOffset;

    const lockedVisibleHeight = Math.max(0, totalHeight - clampedOffset);
    overlayLockedVisibleHeight.value = lockedVisibleHeight;
    setVisibleHeaderHeight(lockedVisibleHeight);
  };

  const measureVisibleHeaderHeight = () => {
    if (typeof window === "undefined" || !headerRef.value) return;

    if (isOverlayActiveNow()) {
      if (overlayLockedVisibleHeight.value !== null) {
        setVisibleHeaderHeight(overlayLockedVisibleHeight.value);
      }
      return;
    }

    setVisibleHeaderHeight(
      Math.max(0, headerHeight.value - headerOffset.value)
    );
  };

  const resetHeaderOffset = (value: number) => {
    if (typeof window !== "undefined" && headerOffsetRaf !== null) {
      window.cancelAnimationFrame(headerOffsetRaf);
      headerOffsetRaf = null;
    }
    headerOffset.value = value;
    targetHeaderOffset.value = value;
    measureVisibleHeaderHeight();
  };

  const setHeaderOffsetForScroll = (value: number, totalHeight: number) => {
    if (typeof window !== "undefined" && headerOffsetRaf !== null) {
      window.cancelAnimationFrame(headerOffsetRaf);
      headerOffsetRaf = null;
    }

    headerOffset.value = value;
    targetHeaderOffset.value = value;
    setVisibleHeaderHeight(Math.max(0, totalHeight - value));
  };

  const animateHeaderOffsetToTarget = () => {
    if (typeof window === "undefined") return;
    if (headerOffsetRaf !== null) return;

    const tick = () => {
      const diff = targetHeaderOffset.value - headerOffset.value;

      if (Math.abs(diff) <= OFFSET_SNAP) {
        headerOffset.value = targetHeaderOffset.value;
        headerOffsetRaf = null;
        measureVisibleHeaderHeight();
        return;
      }

      headerOffset.value += diff * OFFSET_EASE;
      measureVisibleHeaderHeight();
      headerOffsetRaf = window.requestAnimationFrame(tick);
    };

    headerOffsetRaf = window.requestAnimationFrame(tick);
  };

  const snapHeaderOffsetIfIdle = () => {
    const totalHeight = headerHeight.value;
    if (totalHeight <= 0) return;
    // Never snap mid layout animation — the offset is frozen on purpose.
    if (
      typeof performance !== "undefined" &&
      performance.now() < layoutAnimationFreezeUntil
    ) {
      return;
    }

    const progress = headerOffset.value / totalHeight;
    const targetOffset = progress >= 0.5 ? totalHeight : 0;

    targetHeaderOffset.value = targetOffset;
    animateHeaderOffsetToTarget();

    if (targetOffset >= totalHeight && !isHeaderHidden.value) {
      setHeaderHiddenState(true);
    } else if (targetOffset <= 0 && isHeaderHidden.value) {
      setHeaderHiddenState(false);
    }
  };

  const updateHeaderHeight = () => {
    const header = headerRef.value?.getElementsByClassName("header-wrapper")[0];
    if (header) {
      const height = (header as any).offsetHeight || 0;
      // Skip zero-height measurements — the header is likely display:none
      // (removed from DOM while hidden for Safari safe-area tinting).
      // Keeping the last known height prevents the scroll logic from breaking.
      if (height > 0) {
        setHeaderHeight(height);
        measureVisibleHeaderHeight();
      }
    }
  };

  const persistHeaderState = (scrollPosition: number) => {
    if (typeof window === "undefined") return;

    try {
      sessionStorage.setItem(
        HEADER_SCROLL_STORAGE_KEY,
        JSON.stringify({
          hidden: isHeaderHidden.value,
          scrollY: scrollPosition,
        })
      );
    } catch (error) {
      // eslint-disable-next-line no-console -- non-fatal sessionStorage diagnostic; intentionally a warning
      console.warn("Failed to persist header state", error);
    }
  };

  const setScrollSnapshot = (scrollPosition: number) => {
    lastScrollY.value = scrollPosition;
    persistHeaderState(scrollPosition);
  };

  const restoreHeaderState = () => {
    if (typeof window === "undefined") return;

    try {
      const storedState = sessionStorage.getItem(HEADER_SCROLL_STORAGE_KEY);
      const currentScroll = window.scrollY || 0;
      lastScrollY.value = currentScroll;

      if (storedState) {
        const parsedState = JSON.parse(storedState);
        if (typeof parsedState.scrollY === "number") {
          lastScrollY.value = parsedState.scrollY;
        }

        if (parsedState.hidden && lastScrollY.value > MIN_SCROLL_TO_HIDE) {
          setHeaderHiddenState(true);
        } else {
          setHeaderHiddenState(false);
        }
      } else {
        setScrollSnapshot(currentScroll);
      }
    } catch (error) {
      // eslint-disable-next-line no-console -- non-fatal sessionStorage diagnostic; intentionally a warning
      console.warn("Failed to restore header state", error);
      lastScrollY.value = window.scrollY || 0;
      setHeaderHiddenState(false);
    }
  };

  const updateScrollSnapshot = () => {
    if (typeof window === "undefined") return;
    const currentScroll = window.scrollY || 0;
    setScrollSnapshot(currentScroll);
  };

  watch(isInteractionActive, (active) => {
    if (typeof window === "undefined") return;

    if (active && isHeaderHidden.value) {
      setHeaderHiddenState(false);
    }
    updateScrollSnapshot();
  });

  const lockMobileMenuScroll = () => {
    if (releaseMobileMenuScrollLock) return;

    // Overflow-only, as before, and not announced as an external overlay:
    // that event closes header menus, including this one.
    releaseMobileMenuScrollLock = acquireOverlayScrollLock({
      useFixedPosition: false,
      lockRootOverflow: true,
      notifyOverlayChange: false,
    });
  };

  const releaseMobileMenuScroll = () => {
    const release = releaseMobileMenuScrollLock;
    releaseMobileMenuScrollLock = null;
    release?.();
  };

  watch(isMobileMenuOpen, (isOpen) => {
    if (isOpen) {
      if (mobileMenuResetTimer !== null && typeof window !== "undefined") {
        window.clearTimeout(mobileMenuResetTimer);
        mobileMenuResetTimer = null;
      }
      restoreHeaderToDOM();
      setHeaderHiddenState(false);
      resetHeaderOffset(0);
      lastDirection.value = 0;
      updateScrollSnapshot();
      setVisibleHeaderHeight(headerHeight.value);
      lockMobileMenuScroll();
      return;
    }

    releaseMobileMenuScroll();
    if (typeof window === "undefined") {
      onMobileMenuReset();
      return;
    }

    mobileMenuResetTimer = window.setTimeout(() => {
      mobileMenuResetTimer = null;
      onMobileMenuReset();
    }, MENU_NAVIGATION_DELAY);
  });

  // Remove / restore the header element from rendering so Safari iOS stops
  // sampling its background-color for safe-area tinting.  display:none is
  // applied only while scrolling DOWN with the header already hidden; the
  // moment the user scrolls UP or interacts, display is restored so the
  // slide-in animation plays smoothly.
  const removeHeaderFromDOM = () => {
    if (headerRef.value) {
      (headerRef.value as HTMLElement).style.display = "none";
    }
  };

  const restoreHeaderToDOM = () => {
    if (headerRef.value) {
      (headerRef.value as HTMLElement).style.display = "";
    }
  };

  const setHeaderHiddenState = (hidden: boolean) => {
    if (isHeaderHidden.value === hidden) return;

    if (!hidden) {
      // Restore display BEFORE un-hiding so the slide-in animation is visible.
      restoreHeaderToDOM();
    }

    setHeaderHidden(hidden);

    if (typeof window !== "undefined") {
      skipNextEvaluation = true;
      window.requestAnimationFrame(() => {
        updateScrollSnapshot();
        skipNextEvaluation = false;
      });
    }
  };

  const handleExternalOverlayChange = (count: number) => {
    const isActive = count > 0;

    if (isExternalOverlayActive.value === isActive) return;
    isExternalOverlayActive.value = isActive;

    if (isActive) {
      restoreHeaderToDOM();
      freezeHeaderForOverlay();
      onOverlayActivated();
      return;
    }

    overlayLockedVisibleHeight.value = null;
    if (typeof window !== "undefined") {
      updateScrollSnapshot();
      lastDirection.value = 0;
      skipNextEvaluation = false;
      measureVisibleHeaderHeight();
    }
  };

  const handleOverlayLockChange = (event: Event) => {
    const lockEvent = event as CustomEvent<{ count?: number }>;
    const nextCount = Number(lockEvent.detail?.count || 0);
    handleExternalOverlayChange(nextCount);
  };

  const handleHeaderVisibilityRestore = (event: Event) => {
    const restoreEvent = event as CustomEvent<{ hidden?: boolean }>;
    const hidden = !!restoreEvent.detail?.hidden;
    const totalHeight = getHeaderTotalHeight();

    // Mirror setHeaderHiddenState's DOM handling: when the header is being
    // made visible it must be restored to the DOM (it may have display:none
    // applied by removeHeaderFromDOM from a prior scroll-down-and-hide), or
    // it stays invisible even though isHeaderHidden flips to false.
    if (!hidden) {
      restoreHeaderToDOM();
    }

    skipNextEvaluation = true;
    setHeaderHidden(hidden);
    resetHeaderOffset(hidden ? totalHeight : 0);
    setScrollSnapshot(window.scrollY || 0);

    window.requestAnimationFrame(() => {
      skipNextEvaluation = false;
    });
  };

  const evaluateHeaderVisibility = () => {
    if (typeof window === "undefined") return;

    const docHeight = document.documentElement.scrollHeight || 0;
    // Clamp out iOS rubber-band overscroll at the bottom: during the bounce
    // scrollY overshoots past max and springs back, which reads as
    // "down then up" and flashes the header in/out.
    const maxScroll = Math.max(0, docHeight - window.innerHeight);
    const currentScroll = Math.min(window.scrollY || 0, maxScroll);

    if (isOverlayActiveNow()) {
      return;
    }

    if (skipNextEvaluation) {
      setScrollSnapshot(currentScroll);
      return;
    }

    // Hard freeze while an announced layout animation (accordion open/close)
    // is running. Resync the snapshot so evaluation resumes from an accurate
    // baseline once the freeze lifts.
    const now = performance.now();
    if (now < layoutAnimationFreezeUntil) {
      lastDocumentHeight = docHeight;
      lastScrollY.value = currentScroll;
      return;
    }

    // Absorb scroll events caused by document height mutations (see the
    // declaration of lastDocumentHeight). Only resync the scroll snapshot —
    // WITHOUT persisting or moving the header — so that once the layout is
    // stable again, real user scrolling resumes from an accurate baseline.
    if (docHeight !== lastDocumentHeight) {
      lastDocumentHeight = docHeight;
      lastDocumentHeightChangeAt = now;
      lastScrollY.value = currentScroll;
      return;
    }
    if (now - lastDocumentHeightChangeAt < DOC_MUTATION_SCROLL_GRACE_MS) {
      lastScrollY.value = currentScroll;
      return;
    }

    if (isInteractionActive.value) {
      if (isHeaderHidden.value) {
        setHeaderHiddenState(false);
      }
      restoreHeaderToDOM();
      resetHeaderOffset(0);
      lastDirection.value = 0;
      setScrollSnapshot(currentScroll);
      return;
    }

    if (currentScroll <= TOP_EPSILON) {
      if (isHeaderHidden.value) {
        setHeaderHiddenState(false);
      }
      restoreHeaderToDOM();
      resetHeaderOffset(0);
      lastDirection.value = 0;
      setScrollSnapshot(0);
      return;
    }

    const delta = currentScroll - lastScrollY.value;

    // Ignore tiny scroll jitters (e.g. momentum bleed from horizontal rails like
    // "Selected for you"). Without this, sub-threshold deltas flip the header
    // show/hide and the page visibly jumps. We deliberately do NOT update the
    // scroll snapshot here, so genuine slow scrolling still accumulates and works.
    const MICRO_SCROLL_DELTA = 4;
    if (Math.abs(delta) < MICRO_SCROLL_DELTA) {
      return;
    }

    // When the header is display:none the DOM measurement returns 0.
    // Fall back to the cached headerHeight so scroll evaluation still works.
    const header = headerRef.value?.getElementsByClassName("header-wrapper")[0];
    const measuredHeight = (header as any)?.offsetHeight || 0;
    const totalHeight =
      measuredHeight > 0 ? measuredHeight : headerHeight.value;

    if (totalHeight > 0) {
      const direction: 1 | -1 = delta > 0 ? 1 : -1;

      // Scrolling UP while header is hidden → restore display immediately
      // so the slide-in animation is visible.
      if (direction === -1 && isHeaderHidden.value) {
        restoreHeaderToDOM();
      }

      lastDirection.value = direction;

      let nextOffset = headerOffset.value + delta;

      if (nextOffset < 0) {
        nextOffset = 0;
      } else if (nextOffset > totalHeight) {
        nextOffset = totalHeight;
      }

      setHeaderOffsetForScroll(nextOffset, totalHeight);

      if (nextOffset >= totalHeight && !isHeaderHidden.value) {
        setHeaderHiddenState(true);
      } else if (nextOffset <= 0 && isHeaderHidden.value) {
        setHeaderHiddenState(false);
      }

      // Scrolling DOWN while header is fully hidden → remove from DOM
      // so Safari iOS stops sampling its background for safe-area tinting.
      if (direction === 1 && isHeaderHidden.value) {
        removeHeaderFromDOM();
      }
    }

    setScrollSnapshot(currentScroll);
  };

  const handleScroll = () => {
    if (typeof window === "undefined") return;

    if (isOverlayActiveNow()) {
      return;
    }

    if (scrollIdleTimeout !== null && typeof window !== "undefined") {
      window.clearTimeout(scrollIdleTimeout);
      scrollIdleTimeout = null;
    }

    if (typeof window !== "undefined") {
      scrollIdleTimeout = window.setTimeout(() => {
        scrollIdleTimeout = null;
        snapHeaderOffsetIfIdle();
      }, 500);
    }

    if (scrollRaf !== null) {
      return;
    }

    scrollRaf = window.requestAnimationFrame(() => {
      scrollRaf = null;
      evaluateHeaderVisibility();
    });
  };

  const beginManualVisibleHeightControl = () => {
    releaseVisibleHeightControl = enableManualVisibleHeaderHeightControl();
  };

  // Everything the original component did at the end of onMounted (after data
  // fetches and the first header measurement): observers + window listeners.
  const attachScrollListeners = () => {
    if (
      headerRef.value &&
      typeof window !== "undefined" &&
      "ResizeObserver" in window
    ) {
      resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      resizeObserver.observe(headerRef.value);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", updateHeaderHeight);
    }

    if (typeof window !== "undefined") {
      lastDocumentHeight = document.documentElement.scrollHeight || 0;
      restoreHeaderState();
      handleExternalOverlayChange(getOverlayScrollLockCount());
      window.addEventListener(
        VF_LAYOUT_ANIMATION_EVENT,
        handleLayoutAnimationNotice
      );
      window.addEventListener(
        OVERLAY_LOCK_CHANGE_EVENT,
        handleOverlayLockChange
      );
      window.addEventListener(
        "vf-restore-header-visibility",
        handleHeaderVisibilityRestore
      );
      window.addEventListener("scroll", handleScroll, { passive: true });
      evaluateHeaderVisibility();
      measureVisibleHeaderHeight();
    }
  };

  const detachScrollListeners = () => {
    if (mobileMenuResetTimer !== null) {
      window.clearTimeout(mobileMenuResetTimer);
      mobileMenuResetTimer = null;
    }

    if (resizeObserver && headerRef.value) {
      resizeObserver.unobserve(headerRef.value);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", updateHeaderHeight);
      window.removeEventListener(
        OVERLAY_LOCK_CHANGE_EVENT,
        handleOverlayLockChange
      );
      window.removeEventListener(
        "vf-restore-header-visibility",
        handleHeaderVisibilityRestore
      );
      window.removeEventListener(
        VF_LAYOUT_ANIMATION_EVENT,
        handleLayoutAnimationNotice
      );
      window.removeEventListener("scroll", handleScroll);
      if (scrollRaf) {
        window.cancelAnimationFrame(scrollRaf);
        scrollRaf = null;
      }
      if (headerOffsetRaf !== null) {
        window.cancelAnimationFrame(headerOffsetRaf);
        headerOffsetRaf = null;
      }
      if (scrollIdleTimeout !== null) {
        window.clearTimeout(scrollIdleTimeout);
        scrollIdleTimeout = null;
      }
      lastDirection.value = 0;
      persistHeaderState(window.scrollY || 0);
    }
    if (releaseVisibleHeightControl) {
      releaseVisibleHeightControl();
      releaseVisibleHeightControl = null;
    }
  };

  return {
    headerRef,
    isHeaderHidden,
    placeholderHeight,
    renderedHeaderOffset,
    renderedHeaderOpacity,
    releaseMobileMenuScroll,
    updateHeaderHeight,
    measureVisibleHeaderHeight,
    beginManualVisibleHeightControl,
    attachScrollListeners,
    detachScrollListeners,
  };
}
